import { useEffect, useState } from "react";
import { ArrowUpLeft, MessageCircle } from "lucide-react";
import { contact, products, text } from "../data/content";

const initialForm = {
  name: "",
  phone: "",
  company: "",
  product: "",
  otherProduct: "",
  quantity: "",
  size: "",
  design: "no",
  details: "",
};

export default function QuoteForm({ language, showHeader = false, selectedProductId = "" }) {
  const [form, setForm] = useState(() => ({ ...initialForm, product: selectedProductId }));
  const [fileName, setFileName] = useState("");
  const [status, setStatus] = useState("");
  const selectedProductForDisplay = products.find((item) => item.id === form.product);

  useEffect(() => {
    if (!selectedProductId) return;
    setForm((current) => ({ ...current, product: selectedProductId, otherProduct: "" }));
  }, [selectedProductId]);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = (event) => {
    event.preventDefault();
    const selectedProduct = products.find((item) => item.id === form.product);
    const otherProduct = form.otherProduct.trim();
    const productName = form.product === "other"
      ? `${text(language, "أخرى", "Other")}${otherProduct ? `: ${otherProduct}` : ""}`
      : form.product === "books"
        ? text(language, "كتب / كراسات", "Books / Copybooks")
        : selectedProduct ? text(language, selectedProduct.ar, selectedProduct.en) : text(language, "غير محدد", "Not specified");
    const message = language === "ar"
      ? [
        "السلام عليكم، أرغب في طلب عرض سعر من مطبعة طريق القمر.",
        `الاسم: ${form.name}`,
        `رقم الجوال: ${form.phone}`,
        `اسم النشاط / الشركة: ${form.company || "غير محدد"}`,
        `نوع المنتج: ${productName}`,
        `الكمية التقريبية: ${form.quantity || "غير محددة"}`,
        `المقاس: ${form.size || "غير محدد"}`,
        `هل لدي تصميم جاهز؟ ${form.design === "yes" ? "نعم" : "لا"}`,
        `تفاصيل الطلب: ${form.details || "لا توجد تفاصيل إضافية"}`,
        fileName ? `ملف التصميم المختار: ${fileName} — سأرسله داخل المحادثة.` : "سأرسل ملف التصميم داخل المحادثة إن وجد.",
      ].join("\n")
      : [
        "Hello Moonline, I would like to request a quote.",
        `Name: ${form.name}`,
        `Phone: ${form.phone}`,
        `Business / company: ${form.company || "Not specified"}`,
        `Product: ${productName}`,
        `Approximate quantity: ${form.quantity || "Not specified"}`,
        `Size: ${form.size || "Not specified"}`,
        `Ready artwork? ${form.design === "yes" ? "Yes" : "No"}`,
        `Request details: ${form.details || "No additional details"}`,
        fileName ? `Selected artwork: ${fileName} — I will send it in the chat.` : "I will send artwork in the chat if available.",
      ].join("\n");

    const url = `https://wa.me/${contact.whatsappNumber}?text=${encodeURIComponent(message)}`;
    const opened = window.open(url, "_blank", "noopener,noreferrer");
    if (!opened) window.location.assign(url);
    setStatus(text(language, "تم تجهيز الرسالة. راجعها وأرسلها من WhatsApp، وأرفق التصميم هناك إن وجد.", "Your message is ready. Review and send it in WhatsApp, and attach artwork there if available."));
  };

  return (
    <form className="quote-form" onSubmit={submit} data-testid="quote-form">
      {showHeader && (
        <div className="contact-form-head">
          <span className="eyebrow eyebrow-dark">RFQ / 01</span>
          <h2>{text(language, "اطلب عرض سعر", "Request a quote")}</h2>
          <p>{text(language, "أكمل الحقول الأساسية، ثم ستفتح لك رسالة جاهزة في WhatsApp.", "Complete the essentials and a prefilled WhatsApp message will open.")}</p>
        </div>
      )}
      <div className="form-field">
        <label htmlFor="quote-name">{text(language, "الاسم", "Name")} <span>*</span></label>
        <input id="quote-name" type="text" required value={form.name} onChange={(event) => update("name", event.target.value)} data-testid="quote-name-input" />
      </div>
      <div className="form-field">
        <label htmlFor="quote-phone">{text(language, "رقم الجوال", "Phone")} <span>*</span></label>
        <input id="quote-phone" type="tel" inputMode="tel" required value={form.phone} onChange={(event) => update("phone", event.target.value)} data-testid="quote-phone-input" />
      </div>
      <div className="form-field">
        <label htmlFor="quote-company">{text(language, "اسم النشاط / الشركة", "Business / company")}</label>
        <input id="quote-company" type="text" value={form.company} onChange={(event) => update("company", event.target.value)} data-testid="quote-company-input" />
      </div>
      <div className="form-field">
        <label htmlFor="quote-product">{text(language, "نوع المنتج", "Product type")} <span>*</span></label>
        <select id="quote-product" required value={form.product} onChange={(event) => update("product", event.target.value)} data-testid="quote-product-select">
          <option value="">{text(language, "اختر المنتج", "Choose a product")}</option>
          {products.map((product) => <option key={product.id} value={product.id}>{text(language, product.ar, product.en)}</option>)}
          <option value="books">{text(language, "كتب / كراسات", "Books / Copybooks")}</option>
          <option value="other">{text(language, "أخرى", "Other")}</option>
        </select>
      </div>
      {selectedProductForDisplay && (
        <div className="form-field form-field-full selected-product-field" data-testid="quote-selected-product">
          <span className="selected-product-label">{text(language, "المنتج المختار", "Selected product")}</span>
          <output>{text(language, selectedProductForDisplay.ar, selectedProductForDisplay.en)}</output>
        </div>
      )}
      {form.product === "other" && (
        <div className="form-field form-field-full other-product-field">
          <label htmlFor="quote-other-product">{text(language, "اكتب نوع المنتج", "Describe the product type")}</label>
          <textarea id="quote-other-product" rows="2" placeholder={text(language, "مثال: علب قطع غيار خاصة", "Example: custom spare-parts boxes")} value={form.otherProduct} onChange={(event) => update("otherProduct", event.target.value)} data-testid="quote-other-product-input" />
        </div>
      )}
      <div className="form-field">
        <label htmlFor="quote-quantity">{text(language, "الكمية التقريبية", "Approximate quantity")}</label>
        <input id="quote-quantity" type="text" inputMode="numeric" value={form.quantity} onChange={(event) => update("quantity", event.target.value)} data-testid="quote-quantity-input" />
      </div>
      <div className="form-field">
        <label htmlFor="quote-size">{text(language, "المقاس", "Size")}</label>
        <input id="quote-size" type="text" placeholder={text(language, "مثال: 20 × 20 × 10 سم", "Example: 20 × 20 × 10 cm")} value={form.size} onChange={(event) => update("size", event.target.value)} data-testid="quote-size-input" />
      </div>
      <fieldset className="form-field form-field-full design-choice">
        <legend>{text(language, "هل لديك تصميم جاهز؟", "Do you have ready artwork?")}</legend>
        <div className="radio-group">
          <label><input type="radio" name="design" value="yes" checked={form.design === "yes"} onChange={(event) => update("design", event.target.value)} data-testid="quote-design-yes" /> {text(language, "نعم", "Yes")}</label>
          <label><input type="radio" name="design" value="no" checked={form.design === "no"} onChange={(event) => update("design", event.target.value)} data-testid="quote-design-no" /> {text(language, "لا", "No")}</label>
        </div>
      </fieldset>
      <div className="form-field form-field-full">
        <label htmlFor="quote-details">{text(language, "تفاصيل الطلب", "Request details")}</label>
        <textarea id="quote-details" rows="4" value={form.details} onChange={(event) => update("details", event.target.value)} data-testid="quote-details-input" />
      </div>
      <div className="form-field form-field-full file-field">
        <label htmlFor="quote-artwork">{text(language, "إرفاق التصميم", "Attach artwork")}</label>
        <input id="quote-artwork" type="file" accept="image/*,.pdf,.ai,.psd" onChange={(event) => setFileName(event.target.files?.[0]?.name || "")} data-testid="quote-artwork-input" />
        <small>{fileName || text(language, "اختياري — أرفق الملف داخل محادثة WhatsApp بعد فتحها.", "Optional — attach the file in WhatsApp after it opens.")}</small>
      </div>
      <div className="form-field-full form-submit-row">
        <button type="submit" className="button button-accent form-submit" data-testid="quote-submit-button">
          <MessageCircle size={17} strokeWidth={1.6} />
          {text(language, "إرسال الطلب عبر WhatsApp", "Send request via WhatsApp")}
          <ArrowUpLeft size={16} strokeWidth={1.5} />
        </button>
        <p className="form-footnote">{text(language, "تُفتح رسالة جاهزة في WhatsApp للمراجعة والإرسال.", "A prefilled WhatsApp message opens for your review and sending.")}</p>
      </div>
      {status && <p className="form-status form-field-full" role="status" data-testid="quote-status">{status}</p>}
    </form>
  );
}
